import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  HostListener,
  OnChanges,
  Output,
  SimpleChanges,
  effect,
  inject,
  input,
} from '@angular/core';
import { FormsModule, NgModel } from '@angular/forms';
import { Subscription } from 'rxjs';
import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import { UiPanelComponent } from '@template-ui/components';
import { TypedCapabilityInputsComponent } from '@app/shared/forms/typed-capability-inputs.component';
import { PlatformWorkflowDefinitionLifecycleService } from './platform-workflow-definition-lifecycle.service';
import type {
  WorkflowAutomatedStep,
  WorkflowCapabilityReference,
  WorkflowDefinitionDocument,
} from './platform-workflow-definition-lifecycle.models';
import {
  capabilityIdentity,
  sameCapabilityReference,
} from './workflow-capability-authoring';
import type {
  AutomationField,
  WorkflowAutomationCatalog,
} from './workflow-automation-authoring';
import {
  applyAutomatedStep,
  automatedLiteralError,
  automatedInputContextCompatible,
  automatedStepFingerprint,
  type AutomatedOutcomeEdit,
} from './workflow-automated-step-authoring';
import type { WorkflowEditorPendingScope, WorkflowEditorBufferState } from './workflow-editor-pending-state';
import { WorkflowOwnerViewState } from './workflow-owner-view-state';

/** The existing automated workstation sections; null live availability means hidden. */
export type WorkflowAutomatedStepView = 'activity' | 'routing' | 'settings';

interface AutomatedResultRow {
  commandOutcome: string;
  outcome: AutomatedOutcomeEdit | null;
  index: number;
  key: string;
}

interface AutomatedFocusTarget {
  section: 'activity' | 'routing';
  field?: string;
  commandOutcome?: string;
  control?: 'route' | 'destination' | 'source' | 'literal' | 'context';
}

@Component({
  selector: 'phms-workflow-automated-step-editor',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    UiButtonDirective,
    UiControlDirective,
    UiPanelComponent,
    TypedCapabilityInputsComponent,
  ],
  templateUrl: './workflow-automated-step-editor.component.html',
  styleUrl: './workflow-automated-step-editor.component.scss',
})
export class WorkflowAutomatedStepEditorComponent implements OnChanges {
  @Input({ required: true }) document!: WorkflowDefinitionDocument;
  @Input({ required: true }) nodeId = '';
  /** Optional focused presentation; default retains the complete existing editor. */
  readonly section = input<string>('all');
  /** Focuses one exact existing result route after an explicit owner handoff. */
  readonly routeFocus = input<{ routeOutcome: string; request: number; commandOutcome?: string } | null>(null);
  @Input() latestDocument: (() => WorkflowDefinitionDocument | null) | null = null;
  @Input() ownerContext: (() => unknown) | null = null;
  @Input() viewContext: (() => unknown) | null = null;
  @Input() activeView: (() => WorkflowAutomatedStepView | null) | null = null;
  @Input() canAuthor = false;
  @Input() interactionBlocked = false;
  @Output() documentChange = new EventEmitter<WorkflowDefinitionDocument>();
  @Output() pendingChangesChange = new EventEmitter<boolean>();
  @Output() hearingLifecycleSettings = new EventEmitter<void>();
  @Output() revealSection = new EventEmitter<{ nodeId: string; section: 'activity' | 'routing'; owner: string; source: string; view: string | null }>();
  @Output() openOutcomePath = new EventEmitter<{ activity: WorkflowCapabilityReference; commandOutcome: string; routeOutcome: string; owner: string; source: string; view: string | null }>();
  private readonly api = inject(PlatformWorkflowDefinitionLifecycleService);
  private readonly destroy = inject(DestroyRef);
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly ownerViews = inject(WorkflowOwnerViewState, { optional: true }) ?? new WorkflowOwnerViewState();
  catalog: WorkflowAutomationCatalog | null = null;
  automation: WorkflowAutomatedStep | null = null;
  outcomes: AutomatedOutcomeEdit[] = [];
  loading = false;
  pending = false;
  error = '';
  loadError = '';
  private original = '';
  private originalContext = '';
  private catalogKey = '';
  private request = 0;
  private subscription?: Subscription;
  private focusedRouteRequest: { routeOutcome: string; request: number; commandOutcome?: string } | null = null;
  private routeFocusContext: {
    request: { routeOutcome: string; request: number; commandOutcome?: string };
    nodeId: string;
    fingerprint: string;
    owner: string;
  } | null = null;
  private ownerSnapshot = '';
  private registeredOwner = false;
  private presentationKey = '';
  private selectedCode: string | undefined;
  private selectedPin = '';
  private generation = 0;
  private focusRequest = 0;
  private pendingCatalog = '';
  private restoringModel = false;
  private readonly fieldRows = new WeakMap<
    AutomationField,
    AutomationField[]
  >();
  readonly standalone = { standalone: true };
  constructor() {
    effect(() => {
      this.routeFocus();
      this.section();
      this.focusRequestedResultRoute();
    });
    this.destroy.onDestroy(() => {
      this.rememberSelection();
      this.focusRequest++;
      this.request++;
      this.subscription?.unsubscribe();
    });
  }
  ngOnChanges(changes: SimpleChanges = {}): void {
    const priorFocus = this.element.nativeElement.ownerDocument.activeElement;
    const focusedCode = priorFocus instanceof HTMLElement && this.element.nativeElement.contains(priorFocus) ? priorFocus.getAttribute('data-automated-result') : null;
    const oldView = this.presentationKey, oldPin = this.operationKey;
    const firstOwner = !!this.ownerContext && !this.registeredOwner;
    if (!this.pending && (!this.original || changes['document'] || changes['nodeId'] || firstOwner)) {
      this.ownerSnapshot = this.ownerKey();
      this.registeredOwner ||= !!this.ownerContext;
      this.reset();
    }
    const key = this.currentPresentationKey();
    if (key !== this.presentationKey) {
      this.rememberSelection();
      this.presentationKey = key;
      const retained = this.ownerViews.read<{ code: string | undefined; pin: string }>(key);
      this.selectedCode = retained?.code;
      this.selectedPin = retained?.pin ?? '';
      this.focusRequest++;
    }
    this.loadCatalog();
    this.reconcileSelection();
    this.focusRequestedResultRoute();
    if (changes['document'] && focusedCode && oldView === this.presentationKey && oldPin === this.operationKey && focusedCode === this.selectedCode)
      this.restoreRetainedResultFocus(focusedCode, priorFocus);
  }
  private reset(): void {
    this.generation++;
    this.focusRequest++;
    const nodes = this.document?.nodes.filter((item) => item?.id === this.nodeId) ?? [];
    const node = nodes.length === 1 ? nodes[0] : null;
    this.automation = node?.automation
      ? structuredClone(node.automation)
      : null;
    this.outcomes = (
      Array.isArray(this.automation?.outcomes) ? this.automation.outcomes : []
    ).filter(outcome => !!outcome && typeof outcome === 'object').map((outcome) => ({
      ...structuredClone(outcome),
      destinationNodeId:
        this.document.edges.filter(
          (edge) =>
            edge?.fromNodeId === this.nodeId &&
            edge.outcomeCode === outcome.routeOutcome,
        ).length === 1 ? this.document.edges.find(edge => edge?.fromNodeId === this.nodeId && edge.outcomeCode === outcome.routeOutcome)!.toNodeId : '',
    }));
    this.original = this.sourceFingerprint(this.document);
    this.originalContext = this.documentContext(this.document);
    this.pendingCatalog = '';
    this.error = '';
  }
  loadCatalog(force = false): void {
    const key = JSON.stringify([
      this.document.workflowCode,
      this.document.packReference,
      this.ownerKey(),
    ]);
    if (!force && key === this.catalogKey) return;
    this.catalogKey = key;
    const request = ++this.request;
    const owner = this.ownerKey();
    this.subscription?.unsubscribe();
    this.catalog = null;
    this.loading = true;
    this.loadError = '';
    this.subscription = this.api
      .getAutomatedActivityCatalog(this.document)
      .subscribe({
        next: (catalog) => {
          if (request !== this.request || key !== this.catalogKey || owner !== this.ownerKey() || this.destroy.destroyed) return;
          this.loading = false;
          if (catalog.contractVersion !== 1) {
            this.loadError =
              'This activity catalog version is unavailable. Retained settings are kept.';
            this.focusRequestedResultRoute(); return;
          }
          this.catalog = catalog;
          this.reconcileSelection();
          this.focusRequestedResultRoute();
        },
        error: () => {
          if (request !== this.request || owner !== this.ownerKey() || this.destroy.destroyed) return;
          this.loading = false;
          this.loadError =
            'Installed activities could not be checked. Your settings are kept; retry availability before applying.';
          this.focusRequestedResultRoute();
        },
      });
  }
  get operationKey(): string {
    return this.automation?.command?.key
      ? this.operationId(this.automation.command)
      : '';
  }
  operationId(command: WorkflowAutomatedStep['command']): string {
    return JSON.stringify([
      capabilityIdentity(command.key),
      command.inputSchemaHash,
      command.outputSchemaHash,
      command.contractHash,
    ]);
  }
  get operation() {
    const command = this.automation?.command;
    const matches = Array.isArray(this.catalog?.operations) && command?.key
      ? this.catalog.operations.filter(item => item?.command?.key && capabilityIdentity(item.command.key) === capabilityIdentity(command.key)) : [];
    const match = matches.length === 1 && sameCapabilityReference(matches[0].command, command!) ? matches[0] : null;
    return match && Array.isArray(match.input?.fields) && Array.isArray(match.output?.fields) && Array.isArray(match.outcomes)
      && match.outcomes.every(code => typeof code === 'string' && !!code.trim()) && new Set(match.outcomes).size === match.outcomes.length ? match : null;
  }
  get unavailableActivities() {
    return (
      (Array.isArray(this.catalog?.operations) ? this.catalog.operations : []).filter((item) => !item.availableForNewUse)
    );
  }
  get isHearingEligibility(): boolean {
    const key = this.operation?.command.key;
    return (
      key?.provider === 'hearings' &&
      key.kind === 'command' &&
      key.code === 'eligibility.workflow.evaluate' &&
      key.version === 1
    );
  }
  readableName(value: unknown): string {
    const name = typeof value === 'string' ? value : '';
    const words = name
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/_/g, ' ');
    return words ? words[0].toUpperCase() + words.slice(1) : name;
  }
  get disabled(): boolean {
    return (
      !this.canAuthor ||
      this.interactionBlocked ||
      this.loading ||
      !this.catalog || !this.ownerCurrent()
    );
  }
  get settingsDisabled(): boolean {
    return (
      this.disabled ||
      !this.operation?.availableForNewUse ||
      !this.operation.packPermitted ||
      this.automation?.version !== 1 ||
      !!this.structureIssue
    );
  }
  get destinations() {
    return this.document.nodes.filter(
      (node) =>
        node.id !== this.nodeId &&
        [
          'human_step',
          'automated_step',
          'routing_decision',
          'terminal',
        ].includes(node.typeCode),
    );
  }
  selectOperation(key: string, model?: NgModel): void {
    this.chooseOperation(key);
    if (model) this.restoreModel(model, this.operationKey);
  }
  private chooseOperation(key: string): void {
    if (this.disabled || !this.nativeCurrent('activity')) return;
    if (this.automation && this.structureIssue) { this.error = this.structureIssue; return; }
    const matches = this.catalog?.operations.filter(
      (item) =>
        this.operationId(item.command) === key && item.availableForNewUse,
    ) ?? [];
    const operation = matches.length === 1 ? matches[0] : null;
    if (!operation || this.catalog!.operations.filter(item => capabilityIdentity(item.command.key) === capabilityIdentity(operation.command.key)).length !== 1) return;
    if (
      this.automation?.command?.key &&
      sameCapabilityReference(this.automation.command, operation.command)
    )
      return;
    const retainedNode = this.document.nodes.find(
      (node) => node.id === this.nodeId,
    );
    const retainedEdges = this.document.edges.filter(
      (edge) => edge.fromNodeId === this.nodeId,
    );
    const provisionalDestination =
      !retainedNode?.automation &&
      retainedEdges.length === 1 &&
      retainedEdges[0].pathKindCode == null &&
      retainedEdges[0].outcomeCode == null
        ? retainedEdges[0].toNodeId
        : '';
    const completions = this.destinations.filter(
      (node) => node.typeCode === 'terminal',
    );
    const completionDestination = completions.some(
      (node) => node.id === provisionalDestination,
    )
      ? provisionalDestination
      : completions.length === 1
        ? completions[0].id
        : '';
    this.automation = {
      version: 1,
      command: structuredClone(operation.command),
      inputs: [],
      outcomes: [],
    };
    this.outcomes = operation.outcomes.map((outcome, index) => ({
      commandOutcome: outcome,
      routeOutcome: outcome,
      destinationNodeId:
        index === 0 && provisionalDestination
          ? provisionalDestination
          : completionDestination,
    }));
    this.selectedCode = operation.outcomes[0];
    this.selectedPin = this.operationKey;
    this.generation++;
    this.rememberSelection();
    this.changed();
  }
  input(field: string) {
    return Array.isArray(this.automation?.inputs)
      ? this.automation.inputs.find((input) => input?.field === field)
      : undefined;
  }
  singleField(field: AutomationField): AutomationField[] {
    let rows = this.fieldRows.get(field);
    if (!rows) {
      rows = [{ ...field, required: true }];
      this.fieldRows.set(field, rows);
    }
    return rows;
  }
  literal(field: string): Record<string, unknown> {
    return { [field]: this.input(field)?.value };
  }
  usesExactLiteral(field: AutomationField): boolean {
    return (
      ['number', 'integer', 'array', 'object'].includes(field.type) ||
      this.input(field.name)?.literalJson != null
    );
  }
  exactLiteral(field: string): string {
    const input = this.input(field);
    return input?.literalJson ?? JSON.stringify(input?.value) ?? '';
  }
  setExactLiteral(field: string, text: string, origin?: AutomationField, model?: NgModel): void {
    if (this.restoringModel) return;
    if (this.settingsDisabled || !this.nativeCurrent('activity') || origin && !this.currentField(origin)) { if (model) this.restoreModel(model, this.exactLiteral(field)); return; }
    const input = this.input(field);
    if (input?.source !== 'literal') { if (model) this.restoreModel(model, this.exactLiteral(field)); return; }
    input.value = null;
    input.literalJson = text;
    delete input.context;
    this.changed();
  }
  fieldKey(_index: number, field: AutomationField): string {
    return field.name;
  }
  contexts(field: AutomationField) {
    return (
      this.catalog?.contextBindings.filter((context) =>
        automatedInputContextCompatible(field, context, this.document),
      ) ?? []
    );
  }
  contextAvailable(field: AutomationField): boolean {
    return this.contexts(field).some(
      (context) => context.code === this.input(field.name)?.context,
    );
  }
  setSource(field: AutomationField, source: string, model?: NgModel): void {
    if (this.restoringModel) return;
    if (this.settingsDisabled || !this.automation || !this.nativeCurrent('activity') || !this.currentField(field)) { if (model) this.restoreModel(model, this.input(field.name)?.source ?? 'omitted'); return; }
    if (!['literal', 'context'].includes(source) && !(source === 'omitted' && !field.required)) { if (model) this.restoreModel(model, this.input(field.name)?.source ?? 'omitted'); return; }
    this.automation.inputs = this.automation.inputs.filter(
      (input) => input.field !== field.name,
    );
    if (
      source === 'literal' &&
      ['number', 'integer', 'array', 'object'].includes(field.type)
    )
      this.automation.inputs.push({
        field: field.name,
        source,
        value: null,
        literalJson:
          field.type === 'array' ? '[]' : field.type === 'object' ? '{}' : '0',
      });
    else if (source === 'literal')
      this.automation.inputs.push({
        field: field.name,
        source,
        value:
          field.type === 'boolean'
            ? false
            : field.type === 'array'
              ? []
              : field.type === 'string'
                ? ''
                : 0,
      });
    else if (source === 'context')
      this.automation.inputs.push({ field: field.name, source, context: '' });
    this.changed();
  }
  setLiteral(field: string, values: Record<string, unknown>, origin?: AutomationField): void {
    if (this.settingsDisabled || !this.nativeCurrent('activity') || origin && !this.currentField(origin)) return;
    const input = this.input(field);
    if (input?.source === 'literal') {
      delete input.literalJson;
      input.value = values[field];
      this.changed();
    }
  }
  setContext(field: string, context: string, origin?: AutomationField, model?: NgModel): void {
    if (this.restoringModel) return;
    if (this.settingsDisabled || !this.nativeCurrent('activity') || origin && !this.currentField(origin)) { if (model) this.restoreModel(model, this.input(field)?.context ?? ''); return; }
    const input = this.input(field);
    if (input?.source === 'context') {
      input.context = context;
      this.changed();
    }
  }
  changed(): void {
    if (!this.pending) this.pendingCatalog = JSON.stringify(this.catalog);
    this.pending = true;
    this.error = '';
    this.pendingChangesChange.emit(true);
  }
  hasPendingChanges(): boolean {
    return this.pending;
  }
  private ownerKey(): string { return this.ownerContext ? JSON.stringify(this.ownerContext()) : ''; }
  private currentDocument(): WorkflowDefinitionDocument | null { return this.latestDocument ? this.latestDocument() : this.document; }
  private ownerCurrent(): boolean { return !this.destroy.destroyed && this.ownerKey() === this.ownerSnapshot; }
  private sourceFingerprint(document: WorkflowDefinitionDocument): string {
    try { return automatedStepFingerprint(document, this.nodeId); } catch { return JSON.stringify([document, this.nodeId]); }
  }
  private currentPresentationKey(): string {
    return this.document ? JSON.stringify([this.ownerViews.key(this.document, this.nodeId, 'automated-results'), this.viewContext?.() ?? this.ownerContext?.() ?? null]) : '';
  }
  private sourceCurrent(): boolean {
    const document = this.currentDocument();
    return !!document && this.documentContext(document) === this.originalContext && this.sourceFingerprint(document) === this.original;
  }
  private currentView(): string | null { return this.activeView ? this.activeView() : this.section(); }
  private presentationCurrent(view?: WorkflowAutomatedStepView): boolean {
    const current = this.currentView();
    return this.ownerCurrent() && this.presentationKey === this.currentPresentationKey()
      && current !== null && (view === undefined || current === 'all' || current === view)
      && !this.interactionBlocked && !this.ownerViews.focusSuspended()
      && !this.element.nativeElement.closest('[hidden],[inert]');
  }
  private nativeCurrent(view?: WorkflowAutomatedStepView): boolean {
    return this.presentationCurrent(view) && this.sourceCurrent() && (!this.pending || this.pendingCatalog === JSON.stringify(this.catalog));
  }
  private currentField(field: AutomationField): boolean { return this.operation?.input.fields.includes(field) === true; }
  private rememberSelection(): void {
    if (this.presentationKey) this.ownerViews.remember(this.presentationKey, { code: this.selectedCode, pin: this.selectedPin });
  }
  private reconcileSelection(): void {
    if (this.selectedCode !== undefined || !this.operation) return;
    const first = this.resultRows[0];
    if (first) { this.selectedCode = first.commandOutcome; this.selectedPin = this.operationKey; this.rememberSelection(); }
  }
  /** Unsupported retained content stays inspectable and cannot enter the strict writer. */
  get structureIssue(): string {
    const object = (value: unknown, keys: readonly string[]): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).every(key => keys.includes(key));
    const automation: unknown = this.automation;
    if (!object(automation, ['version', 'command', 'inputs', 'outcomes']) || !Array.isArray(automation['inputs']) || !Array.isArray(automation['outcomes']))
      return 'The retained activity settings are unavailable for editing. Their original values are preserved.';
    const command = automation['command'];
    const key = object(command, ['key', 'inputSchemaHash', 'outputSchemaHash', 'contractHash']) ? command['key'] : null;
    if (!object(command, ['key', 'inputSchemaHash', 'outputSchemaHash', 'contractHash']) || !object(key, ['provider', 'kind', 'code', 'version'])
      || !['provider', 'kind', 'code'].every(name => typeof key[name] === 'string') || typeof key['version'] !== 'number'
      || !['inputSchemaHash', 'outputSchemaHash', 'contractHash'].every(key => typeof command[key] === 'string'))
      return 'The retained exact activity reference is unsupported. Its saved values are preserved.';
    if (automation['inputs'].some(input => !object(input, ['field', 'source', 'value', 'literalJson', 'context']) || typeof input['field'] !== 'string'
      || !['literal', 'context'].includes(String(input['source'])) || (input['literalJson'] != null && typeof input['literalJson'] !== 'string')
      || (input['context'] != null && typeof input['context'] !== 'string'))
      || automation['outcomes'].some(outcome => !object(outcome, ['commandOutcome', 'routeOutcome']) || typeof outcome['commandOutcome'] !== 'string' || typeof outcome['routeOutcome'] !== 'string'))
      return 'These retained activity inputs or result mappings contain unsupported content. They are kept read-only.';
    const operation = this.operation;
    if (operation && (!Array.isArray(operation.outcomes) || operation.outcomes.some(code => typeof code !== 'string' || !code.trim())
      || new Set(operation.outcomes).size !== operation.outcomes.length || !Array.isArray(operation.input?.fields)))
      return 'The installed activity result or input identities are unavailable or ambiguous.';
    if (new Set(this.outcomes.map(outcome => outcome.commandOutcome)).size !== this.outcomes.length)
      return 'The retained activity result identities are ambiguous. Inspect the saved paths before changing settings.';
    if (operation && (this.outcomes.length !== operation.outcomes.length || this.outcomes.some(outcome => !operation.outcomes.includes(outcome.commandOutcome))))
      return 'The retained activity result mappings are incomplete or unsupported. All installed results remain inspectable; no mapping is invented.';
    const outgoing = this.document.edges.filter(edge => edge?.fromNodeId === this.nodeId);
    if (outgoing.some(edge => edge.pathKindCode != null) || this.outcomes.some(outcome => outgoing.filter(edge => edge.outcomeCode === outcome.routeOutcome).length > 1))
      return 'The retained activity paths are unsupported or ambiguous. Inspect them on the process map; saved paths are preserved.';
    return '';
  }
  get resultRows(): AutomatedResultRow[] {
    const rows: AutomatedResultRow[] = this.outcomes.map((outcome, index) => ({ commandOutcome: typeof outcome.commandOutcome === 'string' ? outcome.commandOutcome : '', outcome, index, key: `saved:${index}` }));
    for (const [index, code] of (this.operation?.outcomes ?? []).entries()) {
      if (!rows.some(row => row.commandOutcome === code)) rows.push({ commandOutcome: code, outcome: null, index, key: `missing:${index}` });
    }
    return rows;
  }
  readonly trackResult = (_index: number, row: AutomatedResultRow): string => JSON.stringify([this.operationKey,
    this.resultRows.filter(item => item.commandOutcome === row.commandOutcome).length === 1 ? row.commandOutcome : row.key]);
  resultSelectable(row: AutomatedResultRow): boolean { return !!row.commandOutcome && this.rowCurrent(row); }
  get selectedResult(): AutomatedResultRow | null {
    if (this.selectedPin !== this.operationKey) return null;
    const rows = this.resultRows.filter(row => row.commandOutcome === this.selectedCode);
    return rows.length === 1 ? rows[0] : null;
  }
  isResultSelected(row: AutomatedResultRow): boolean { return !!this.selectedResult && row.commandOutcome === this.selectedCode; }
  selectResult(row: AutomatedResultRow): void {
    if (!this.nativeCurrent('routing') || !this.rowCurrent(row)) return;
    this.focusRequest++; this.selectedCode = row.commandOutcome; this.selectedPin = this.operationKey; this.rememberSelection();
  }
  private rowCurrent(row: AutomatedResultRow): boolean {
    const rows = this.resultRows.filter(item => item.commandOutcome === row.commandOutcome);
    return rows.length === 1 && rows[0].outcome === row.outcome && rows[0].key === row.key;
  }
  private savedEdges(row: AutomatedResultRow) {
    const document = this.currentDocument();
    const mappings = document?.nodes.filter(node => node?.id === this.nodeId);
    const saved = mappings?.length === 1 && Array.isArray(mappings[0].automation?.outcomes)
      ? mappings[0].automation!.outcomes.filter(outcome => outcome?.commandOutcome === row.commandOutcome) : [];
    return saved.length === 1 ? document!.edges.filter(edge => edge?.fromNodeId === this.nodeId && edge.outcomeCode === saved[0].routeOutcome) : [];
  }
  resultIssue(row: AutomatedResultRow): string {
    if (!row.commandOutcome || this.resultRows.filter(item => item.commandOutcome === row.commandOutcome).length !== 1) return 'Ambiguous retained result identity';
    if (!row.outcome) return 'No saved route mapping';
    if (!this.operation?.outcomes.includes(row.commandOutcome)) return 'Unavailable installed result';
    const saved = this.savedEdges(row);
    if (saved.length > 1) return 'Ambiguous saved paths';
    if (saved.some(edge => edge.pathKindCode != null)) return 'Unsupported saved path';
    if (!/^[a-z][a-z0-9._-]{0,119}$/.test(row.outcome.routeOutcome) || this.outcomes.filter(outcome => outcome.routeOutcome === row.outcome!.routeOutcome).length !== 1) return 'Needs a unique route key';
    if (!row.outcome.destinationNodeId) return 'Needs destination';
    if (this.destinations.filter(node => node.id === row.outcome!.destinationNodeId).length !== 1) return `Unavailable saved destination (${row.outcome.destinationNodeId})`;
    return '';
  }
  resultSummary(row: AutomatedResultRow): string {
    const issue = this.resultIssue(row);
    if (issue) return issue;
    const node = this.destinations.find(node => node.id === row.outcome!.destinationNodeId)!;
    return `Continue to ${node.displayName || node.stepCode || (node.typeCode === 'terminal' ? `Complete workflow · ${node.id}` : node.id)}`;
  }
  get missingDestinationCount(): number { return this.resultRows.filter(row => this.resultIssue(row) === 'Needs destination').length; }
  resultEditable(row: AutomatedResultRow): boolean {
    return !this.settingsDisabled && !!row.outcome && this.rowCurrent(row) && this.operation?.outcomes.includes(row.commandOutcome) === true;
  }
  changeDestination(row: AutomatedResultRow, value: string, model: NgModel): void {
    if (this.restoringModel) return;
    if (this.nativeCurrent('routing') && this.isResultSelected(row) && this.resultEditable(row)
      && (!value || this.destinations.filter(node => node.id === value).length === 1)) {
      row.outcome!.destinationNodeId = value; this.changed();
    }
    this.restoreModel(model, this.selectedResult?.outcome?.destinationNodeId ?? row.outcome?.destinationNodeId ?? '');
  }
  changeRouteKey(row: AutomatedResultRow, value: string, model: NgModel): void {
    if (this.restoringModel) return;
    if (this.nativeCurrent('routing') && this.isResultSelected(row) && this.resultEditable(row)) { row.outcome!.routeOutcome = value; this.changed(); }
    this.restoreModel(model, this.selectedResult?.outcome?.routeOutcome ?? row.outcome?.routeOutcome ?? '');
  }
  private restoreModel(model: NgModel, value: string): void {
    if (model.control.value === value && model.viewModel === value) return;
    this.restoringModel = true;
    try { model.control.setValue(value, { emitEvent: false }); } finally { this.restoringModel = false; }
  }
  hasSavedPath(row: AutomatedResultRow): boolean {
    const edges = this.savedEdges(row);
    return this.rowCurrent(row) && edges.length === 1 && edges[0].pathKindCode == null
      && this.currentDocument()!.nodes.filter(node => node?.id === edges[0].toNodeId).length === 1;
  }
  showSelectedPath(): void {
    const row = this.selectedResult;
    if (!row || !this.nativeCurrent('routing')) return;
    const code = row.commandOutcome;
    if (this.pending && !this.applyPendingChanges(this.currentDocument() ?? undefined)) return;
    const current = this.resultRows.find(item => item.commandOutcome === code);
    if (!current || !this.nativeCurrent('routing') || !this.hasSavedPath(current) || !this.automation?.command) return;
    this.openOutcomePath.emit({ activity: structuredClone(this.automation.command), commandOutcome: code, routeOutcome: current.outcome!.routeOutcome, owner: this.ownerKey(), source: this.sourceFingerprint(this.currentDocument()!), view: this.currentView() });
  }
  reviewActivity(): void {
    if (!this.nativeCurrent('routing')) return;
    if (this.pending && !this.applyPendingChanges(this.currentDocument() ?? undefined)) return;
    if (this.nativeCurrent('routing')) this.revealSection.emit({ nodeId: this.nodeId, section: 'activity', owner: this.ownerKey(), source: this.sourceFingerprint(this.currentDocument()!), view: this.currentView() });
  }
  checkActivities(): void { if (this.nativeCurrent('activity')) this.loadCatalog(true); }
  openHearingSettings(): void { if (this.nativeCurrent('activity')) this.hearingLifecycleSettings.emit(); }
  private bufferControlsVisible(): boolean { const view = this.activeView ? this.activeView() : this.section(); return view === 'all' || view === 'activity' || view === 'routing'; }
  applyActivityChanges(): void { if (this.presentationCurrent() && this.bufferControlsVisible()) this.applyPendingChanges(this.currentDocument() ?? undefined); }
  discardActivityChanges(): void { if (this.presentationCurrent() && this.bufferControlsVisible()) this.discardPendingChanges(); }
  /** One inert recovery envelope retains hidden results and exact literal text; it is never replayed. */
  capturePendingRouteInputs(): readonly { name: string; label: string; value: string }[] {
    if (!this.pending) return [];
    return [{ name: `workflow-automated-input:${encodeURIComponent(this.nodeId)}`, label: `Unapplied automated activity and all result routes for ${this.nodeId}`,
      value: JSON.stringify({ format: 'phms.workflow-automated-input', version: 1, nodeId: this.nodeId, activity: this.automation,
        outcomes: this.outcomes, originalSource: this.original, sourceContext: this.originalContext }) }];
  }
  private documentContext(document: WorkflowDefinitionDocument): string {
    return JSON.stringify([document.workflowCode, document.definitionVersionCode, document.packReference]);
  }
  /** Installed activity, inputs and every exact result path form one atomic edit. */
  pendingEditorScopes(latestDocument: () => WorkflowDefinitionDocument | null = () => this.document): readonly WorkflowEditorPendingScope[] {
    return [{
      id: 'automated-activity-and-results', label: 'automated activity and result routes',
      state: (): WorkflowEditorBufferState => {
        if (!this.pending) return 'clean';
        const document = latestDocument();
        if (!document || !this.ownerCurrent() || this.documentContext(document) !== this.originalContext || this.sourceFingerprint(document) !== this.original || this.pendingCatalog !== JSON.stringify(this.catalog)) return 'stale';
        if (this.settingsDisabled || !this.automation) return 'invalid';
        try { applyAutomatedStep(document, this.nodeId, this.automation, this.outcomes, this.catalog!); return 'valid'; } catch { return 'invalid'; }
      },
      fingerprint: () => {
        const document = latestDocument();
        return JSON.stringify([this.pending, this.nodeId, this.original, this.originalContext, this.automation, this.outcomes,
          this.canAuthor, this.interactionBlocked, this.loading, this.catalog,
          this.ownerKey(), this.pendingCatalog, document && [this.documentContext(document), this.sourceFingerprint(document), document.payloadSchemaVersion]]);
      },
      apply: () => { const document = latestDocument(); return !!document && this.applyPendingChanges(document); },
      discard: () => { const document = latestDocument(); if (document) this.document = document; this.discardPendingChanges(); },
      focus: () => this.focusPendingChanges(),
    }];
  }
  applyPendingChanges(document = this.currentDocument() ?? this.document): boolean {
    if (!this.pending) return true;
    if (!this.ownerCurrent() || this.documentContext(document) !== this.originalContext || this.sourceFingerprint(document) !== this.original || this.pendingCatalog !== JSON.stringify(this.catalog)) {
      this.error =
        'The activity source, paths or exact references changed while editing. Your inputs are kept; discard and reopen the current source before applying.';
      this.focusPendingChanges();
      return false;
    }
    if (this.settingsDisabled || !this.automation) { this.error = this.structureIssue || 'The current exact activity is unavailable for editing. Your inputs are retained.'; this.focusPendingChanges(); return false; }
    try {
      const next = applyAutomatedStep(
        document,
        this.nodeId,
        this.automation,
        this.outcomes,
        this.catalog!,
      );
      this.document = next;
      this.pending = false;
      this.pendingChangesChange.emit(false);
      this.original = this.sourceFingerprint(next);
      this.automation = structuredClone(next.nodes.find(node => node.id === this.nodeId)!.automation!);
      this.pendingCatalog = '';
      this.documentChange.emit(next);
      this.error = '';
      return true;
    } catch (error) {
      this.error =
        error instanceof Error ? error.message : 'Review activity settings.';
      this.focusPendingChanges();
      return false;
    }
  }
  discardPendingChanges(): void {
    this.pending = false;
    const current = this.currentDocument(); if (current) this.document = current;
    // Explicit aggregate discard is the trusted recovery boundary; gestures and tab changes never rebase authority.
    this.ownerSnapshot = this.ownerKey();
    this.reset();
    this.ngOnChanges();
    this.pendingChangesChange.emit(false);
  }
  focusPendingChanges(): void {
    if (!this.ownerCurrent()) return;
    const view = this.currentView();
    const target: AutomatedFocusTarget = this.sourceCurrent() ? this.invalidTarget() : { section: 'activity' };
    if (target.commandOutcome) {
      this.selectedCode = target.commandOutcome; this.selectedPin = this.operationKey; this.rememberSelection();
    }
    const owner = this.ownerKey(), source = this.sourceFingerprint(this.currentDocument()!), nodeId = this.nodeId;
    const buffer = JSON.stringify([this.automation, this.outcomes]), catalog = JSON.stringify(this.catalog), pin = this.operationKey;
    const request = ++this.focusRequest, focused = this.element.nativeElement.ownerDocument.activeElement;
    this.revealSection.emit({ nodeId, section: target.section, owner, source, view });
    setTimeout(() => {
      const host = this.element.nativeElement, active = host.ownerDocument.activeElement;
      if (request !== this.focusRequest || owner !== this.ownerKey() || nodeId !== this.nodeId || pin !== this.operationKey
        || !this.presentationCurrent(target.section) || source !== this.sourceFingerprint(this.currentDocument()!) || catalog !== JSON.stringify(this.catalog)
        || buffer !== JSON.stringify([this.automation, this.outcomes]) || !host.isConnected) return;
      if (active !== focused && active !== host.ownerDocument.body && active !== host.ownerDocument.documentElement && active?.isConnected && !active.closest('[hidden],[inert]')) return;
      let control: HTMLElement | null = null;
      if (target.commandOutcome) {
        const detail = Array.from(host.querySelectorAll<HTMLElement>('[data-automated-result-detail]')).find(element => element.dataset['automatedResultDetail'] === target.commandOutcome);
        if (target.control === 'route') { const identity = detail?.querySelector('details'); if (identity) identity.open = true; }
        control = detail?.querySelector<HTMLElement>(target.control === 'route' ? '[data-automated-route-key]' : '[data-automated-destination]') ?? null;
      } else if (target.field) {
        const field = Array.from(host.querySelectorAll<HTMLElement>('[data-automated-input]')).find(element => element.dataset['automatedInput'] === target.field);
        control = field?.querySelector<HTMLElement>(target.control === 'source' ? '[data-automated-source]'
          : target.control === 'context' ? '[data-automated-context]'
          : '[data-automated-literal] input,[data-automated-literal] textarea,input[data-automated-literal],textarea[data-automated-literal]') ?? null;
      }
      if (!control || control.closest('[hidden],[inert]') || control.matches(':disabled'))
        control = Array.from(host.querySelectorAll<HTMLElement>('[role=alert][tabindex], h4')).find(element => !element.closest('[hidden],[inert]')) ?? null;
      if (control && !control.closest('[hidden],[inert]') && !control.matches(':disabled')) control.focus();
    });
  }

  private invalidTarget(): AutomatedFocusTarget {
    const operation = this.operation;
    for (const field of operation?.input.fields ?? []) {
      const input = this.input(field.name);
      if (!input && field.required) return { section: 'activity', field: field.name, control: 'source' };
      if (!input) continue;
      if (!operation!.automationInputs.includes(field.name) || !['literal', 'context'].includes(input.source)
        || input.source === 'literal' && input.context != null || input.source === 'context' && (input.value != null || input.literalJson != null)) return { section: 'activity', field: field.name, control: 'source' };
      if (input.source === 'literal' && automatedLiteralError(field, input)) return { section: 'activity', field: field.name, control: 'literal' };
      if (input.source === 'context' && !this.contexts(field).some(context => context.code === input.context)) return { section: 'activity', field: field.name, control: 'context' };
    }
    const invalid = this.resultRows.find(row => !!this.resultIssue(row));
    return invalid ? { section: 'routing', commandOutcome: invalid.commandOutcome, control: this.resultIssue(invalid).includes('route key') ? 'route' : 'destination' } : { section: 'activity' };
  }

  @HostListener('focusin') cancelObsoleteFocus(): void { this.focusRequest++; }

  private restoreRetainedResultFocus(code: string, previous: Element | null): void {
    const request = ++this.focusRequest, owner = this.ownerKey(), source = this.sourceFingerprint(this.currentDocument()!), pin = this.operationKey;
    setTimeout(() => {
      const host = this.element.nativeElement, active = host.ownerDocument.activeElement;
      if (request !== this.focusRequest || !this.nativeCurrent('routing') || owner !== this.ownerKey() || pin !== this.operationKey
        || source !== this.sourceFingerprint(this.currentDocument()!) || code !== this.selectedCode || !host.isConnected) return;
      if (active !== previous && active !== host.ownerDocument.body && active !== host.ownerDocument.documentElement && active?.isConnected && !active.closest('[hidden],[inert]')) return;
      const rows = Array.from(host.querySelectorAll<HTMLButtonElement>('[data-automated-result]')).filter(row => row.dataset['automatedResult'] === code);
      if (rows.length === 1 && active !== rows[0] && !rows[0].disabled && !rows[0].closest('[hidden],[inert]')) rows[0].focus({ preventScroll: true });
    });
  }

  /** Focus is presentation-only; unknown or shared result identities retain the current focus. */
  private focusRequestedResultRoute(): void {
    const request = this.routeFocus();
    if (
      !request ||
      !this.automation?.command?.key || this.loading ||
      !request.routeOutcome.trim() ||
      request === this.focusedRouteRequest ||
      !['routing', 'all'].includes(this.section())
    ) return;
    if (this.routeFocusContext?.request !== request) {
      this.routeFocusContext = {
        request,
        nodeId: this.nodeId,
        fingerprint: this.sourceFingerprint(this.document),
        owner: this.ownerKey(),
      };
    }
    const context = this.routeFocusContext;
    const nodeId = context.nodeId;
    const focused = this.element.nativeElement.ownerDocument.activeElement, generation = this.focusRequest;
    setTimeout(() => {
      if (
        this.ownerViews.focusSuspended() || this.element.nativeElement.closest('[hidden],[inert]') ||
        this.destroy.destroyed ||
        generation !== this.focusRequest || context.owner !== this.ownerKey() || !this.nativeCurrent('routing') ||
        this.focusedRouteRequest === request ||
        this.routeFocus() !== request ||
        this.nodeId !== nodeId ||
        this.sourceFingerprint(this.document) !== context.fingerprint ||
        !['routing', 'all'].includes(this.section())
      ) return;
      const nodes = this.document.nodes.filter(node => node?.id === nodeId);
      const saved = nodes.length === 1 && nodes[0].typeCode === 'automated_step' && Array.isArray(nodes[0].automation?.outcomes)
        ? nodes[0].automation!.outcomes.filter(outcome => outcome?.routeOutcome === request.routeOutcome)
        : [];
      const current = this.outcomes.filter(outcome => request.commandOutcome ? outcome.commandOutcome === request.commandOutcome : outcome.routeOutcome === request.routeOutcome);
      const edges = this.document.edges.filter(edge => edge?.fromNodeId === nodeId && edge.outcomeCode === request.routeOutcome);
      if (
        saved?.length !== 1 || current.length !== 1 || (request.commandOutcome ? edges.length > 1 : edges.length !== 1) ||
        request.commandOutcome !== undefined && saved[0].commandOutcome !== request.commandOutcome ||
        saved[0].commandOutcome !== current[0].commandOutcome ||
        this.outcomes.filter(outcome => outcome.commandOutcome === current[0].commandOutcome).length !== 1
      ) return;
      const active = this.element.nativeElement.ownerDocument.activeElement;
      if (active !== focused && active !== this.element.nativeElement.ownerDocument.body && active !== this.element.nativeElement.ownerDocument.documentElement && active?.isConnected && !active.closest('[hidden],[inert]')) return;
      this.selectedCode = current[0].commandOutcome; this.selectedPin = this.operationKey; this.rememberSelection();
      const rows = Array.from(this.element.nativeElement.querySelectorAll<HTMLElement>('[data-automated-route-outcome]'))
        .filter(row => row.getAttribute('data-automated-route-outcome') === request.routeOutcome);
      if (rows.length !== 1 || rows[0].closest('[hidden]')) return;
      rows[0].focus();
      this.focusedRouteRequest = request;
    });
  }
}
